import React, { useState, useEffect, Suspense, lazy, type ComponentType } from 'react';
import * as ReactRouterDOM from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { AuthProvider } from './contexts/AuthContext';
import { Role } from './types';
import { ThemeProvider } from './contexts/ThemeContext';
import { CartProvider } from './contexts/CartContext';
import { FavoritesProvider } from './contexts/FavoritesContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { supabaseConfigurationError } from './services/supabaseClient';
import { useAuth } from './hooks/useAuth';
import { useDeviceDetection } from './hooks/useDeviceDetection';
import { useAndroidBackButton } from './hooks/useAndroidBackButton';
import { liveUpdateService } from './services/liveUpdateService';

// Layouts & composants transverses (chargés au démarrage)
import PublicLayout from './components/layout/PublicLayout';
import ClientLayout from './pages/client/ClientLayout';
import AdminLayout from './pages/admin/AdminLayout';
import SecurityDashboard from './pages/admin/SecurityDashboard';
import SplashScreen from './components/common/SplashScreen';
import ScrollToTop from './components/common/ScrollToTop';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { DeviceGuard } from './components/routing/DeviceGuard';
import { MobileOnlyRoute, MobilePublicRoute } from './components/routing/MobileOnlyRoute';
import { PublicLandingRoute } from './components/routing/PublicLandingRoute';
import { RequirePhoneVerified } from './components/routing/RequirePhoneVerified';
import { RequireEmailVerified } from './components/routing/RequireEmailVerified';
import { MobileAdminGuard } from './components/routing/MobileAdminGuard';

/**
 * Lazy avec retry : réessaie l'import dynamique en cas d'échec (réseau, cache).
 * Évite l'erreur "Une erreur est survenue" au premier chargement quand le chunk échoue.
 */
function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  retries = 2,
  interval = 1200
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    let lastErr: unknown;
    for (let i = 0; i <= retries; i++) {
      try {
        return await importFn();
      } catch (e) {
        lastErr = e;
        if (i < retries) await new Promise((r) => setTimeout(r, interval));
      }
    }
    throw lastErr;
  });
}

// Pages publiques (lazy load avec retry)
const LandingPage = lazyWithRetry(() => import('./pages/LandingPage'));
const Login = lazyWithRetry(() => import('./pages/Login'));
const Register = lazyWithRetry(() => import('./pages/Register'));
const ForgotPassword = lazyWithRetry(() => import('./pages/ForgotPassword'));
const ResetPassword = lazyWithRetry(() => import('./pages/ResetPassword'));
const OAuthCallback = lazyWithRetry(() => import('./pages/OAuthCallback'));
const NotFound = lazyWithRetry(() => import('./pages/NotFound'));
const ServicesPage = lazyWithRetry(() => import('./pages/ServicesPage'));
const AboutPage = lazyWithRetry(() => import('./pages/AboutPage'));
const ContactPage = lazyWithRetry(() => import('./pages/ContactPage'));
const FaqPage = lazyWithRetry(() => import('./pages/FaqPage'));
const ProductDetailPage = lazyWithRetry(() => import('./pages/ProductDetailPage'));
const CartPage = lazyWithRetry(() => import('./pages/CartPage'));
const VerifyPhone = lazyWithRetry(() => import('./pages/VerifyPhone'));
const VerifyEmail = lazyWithRetry(() => import('./pages/VerifyEmail'));

// Pages légales (lazy load)
const TermsPage = lazyWithRetry(() => import('./pages/legal/TermsPage'));
const PrivacyPage = lazyWithRetry(() => import('./pages/legal/PrivacyPage'));
const RefundPage = lazyWithRetry(() => import('./pages/legal/RefundPage'));
const DeliveryPage = lazyWithRetry(() => import('./pages/legal/DeliveryPage'));
const LegalNoticePage = lazyWithRetry(() => import('./pages/legal/LegalNoticePage'));
const CookiePolicyPage = lazyWithRetry(() => import('./pages/legal/CookiePolicyPage'));
const RateLimitingPolicyPage = lazyWithRetry(() => import('./pages/legal/RateLimitingPolicyPage'));

// Pages client (lazy load). ClientServices en import direct pour éviter blocage au chargement de la route /dashboard/services
import ClientServices from './pages/client/ClientServices';
const Dashboard = lazyWithRetry(() => import('./pages/client/Dashboard'));
const Deposits = lazyWithRetry(() => import('./pages/client/Deposits'));
const ClientCheckout = lazyWithRetry(() => import('./pages/client/ClientCheckout'));
const ClientOrders = lazyWithRetry(() => import('./pages/client/ClientOrders'));
const Profile = lazyWithRetry(() => import('./pages/client/Profile'));
const ClientSettings = lazyWithRetry(() => import('./pages/client/ClientSettings'));
const SupportTickets = lazyWithRetry(() => import('./pages/client/SupportTickets'));
const TicketDetail = lazyWithRetry(() => import('./pages/client/TicketDetail'));
const MyCodes = lazyWithRetry(() => import('./pages/client/MyCodes'));

// Pages admin (lazy load)
const AdminDashboard = lazyWithRetry(() => import('./pages/admin/AdminDashboard'));
const ManageDeposits = lazyWithRetry(() => import('./pages/admin/ManageDeposits'));
const ManageOrders = lazyWithRetry(() => import('./pages/admin/ManageOrders'));
const ManageUsers = lazyWithRetry(() => import('./pages/admin/ManageUsers'));
const ManageProducts = lazyWithRetry(() => import('./pages/admin/ManageProducts'));
const ManageCategories = lazyWithRetry(() => import('./pages/admin/ManageCategories'));
const ManageMedia = lazyWithRetry(() => import('./pages/admin/ManageMedia'));
const Settings = lazyWithRetry(() => import('./pages/admin/Settings'));
const ManageTickets = lazyWithRetry(() => import('./pages/admin/ManageTickets'));
const AdminTicketDetail = lazyWithRetry(() => import('./pages/admin/AdminTicketDetail'));
const UserDetail = lazyWithRetry(() => import('./pages/admin/UserDetail'));
const AdminProfile = lazyWithRetry(() => import('./pages/admin/AdminProfile'));
const ManagePaymentMethods = lazyWithRetry(() => import('./pages/admin/ManagePaymentMethods'));
const DeliverySettings = lazyWithRetry(() => import('./pages/admin/DeliverySettings'));
const ManageStock = lazyWithRetry(() => import('./pages/admin/ManageStock'));

// Pages mobile (exports nommés → mapping dans lazy)
const MobileLayout = lazyWithRetry(() =>
  import('./pages/mobile/components/MobileLayout').then((m) => ({ default: m.MobileLayout }))
);
const MobileDashboard = lazyWithRetry(() =>
  import('./pages/mobile/MobileDashboard').then((m) => ({ default: m.MobileDashboard }))
);
const ProductCatalog = lazyWithRetry(() =>
  import('./pages/mobile/ProductCatalog').then((m) => ({ default: m.ProductCatalog }))
);
const Wallet = lazyWithRetry(() =>
  import('./pages/mobile/Wallet').then((m) => ({ default: m.Wallet }))
);
const MobileProductDetail = lazyWithRetry(() =>
  import('./pages/mobile/MobileProductDetail').then((m) => ({ default: m.MobileProductDetail }))
);
const MobileProfile = lazyWithRetry(() =>
  import('./pages/mobile/MobileProfile').then((m) => ({ default: m.MobileProfile }))
);
const MobileSecurity = lazyWithRetry(() =>
  import('./pages/mobile/MobileSecurity').then((m) => ({ default: m.MobileSecurity }))
);
const MobileSupport = lazyWithRetry(() =>
  import('./pages/mobile/MobileSupport').then((m) => ({ default: m.MobileSupport }))
);
const MobileOrders = lazyWithRetry(() =>
  import('./pages/mobile/MobileOrders').then((m) => ({ default: m.MobileOrders }))
);
const MobileFavorites = lazyWithRetry(() =>
  import('./pages/mobile/MobileFavorites').then((m) => ({ default: m.MobileFavorites }))
);
const MobileContactPage = lazyWithRetry(() =>
  import('./pages/mobile/MobileContactPage').then((m) => ({ default: m.MobileContactPage }))
);
const MobileAboutPage = lazyWithRetry(() =>
  import('./pages/mobile/MobileAboutPage').then((m) => ({ default: m.MobileAboutPage }))
);
const MobileManageUsers = lazyWithRetry(() =>
  import('./pages/mobile/MobileManageUsers').then((m) => ({ default: m.MobileManageUsers }))
);
const MobileManageDeposits = lazyWithRetry(() =>
  import('./pages/mobile/MobileManageDeposits').then((m) => ({ default: m.MobileManageDeposits }))
);
const MobileManageOrders = lazyWithRetry(() =>
  import('./pages/mobile/MobileManageOrders').then((m) => ({ default: m.MobileManageOrders }))
);
const MobileManageProducts = lazyWithRetry(() =>
  import('./pages/mobile/MobileManageProducts').then((m) => ({ default: m.MobileManageProducts }))
);
const MobileManageStock = lazyWithRetry(() =>
  import('./pages/mobile/MobileManageStock').then((m) => ({ default: m.MobileManageStock }))
);
const MobileManageTickets = lazyWithRetry(() =>
  import('./pages/mobile/MobileManageTickets').then((m) => ({ default: m.MobileManageTickets }))
);
const MobileTicketClients = lazyWithRetry(() =>
  import('./pages/mobile/MobileTicketClients').then((m) => ({ default: m.MobileTicketClients }))
);
const MobileClientConversations = lazyWithRetry(() =>
  import('./pages/mobile/MobileClientConversations').then((m) => ({ default: m.MobileClientConversations }))
);
const MobileConversationMessages = lazyWithRetry(() =>
  import('./pages/mobile/MobileConversationMessages').then((m) => ({ default: m.MobileConversationMessages }))
);
const MobileManageCategories = lazyWithRetry(() =>
  import('./pages/mobile/MobileManageCategories').then((m) => ({ default: m.MobileManageCategories }))
);
const MobileManageMedia = lazyWithRetry(() =>
  import('./pages/mobile/MobileManageMedia').then((m) => ({ default: m.MobileManageMedia }))
);
const MobileCart = lazyWithRetry(() =>
  import('./pages/mobile/MobileCart').then((m) => ({ default: m.MobileCart }))
);
const MobileCheckout = lazyWithRetry(() =>
  import('./pages/mobile/MobileCheckout').then((m) => ({ default: m.MobileCheckout }))
);
const MobileMyCodes = lazyWithRetry(() =>
  import('./pages/mobile/MobileMyCodes').then((m) => ({ default: m.MobileMyCodes }))
);
const MobileLegalDocuments = lazyWithRetry(() =>
  import('./pages/mobile/MobileLegalDocuments').then((m) => ({ default: m.MobileLegalDocuments }))
);

const { Routes, Route, Navigate, Outlet, useSearchParams } = ReactRouterDOM as any;

/** Sur mobile, redirige /services et /services?view=favoris vers catalogue ou favoris pour éviter page blanche. */
const ServicesPageOrRedirect = () => {
  const { isMobile } = useDeviceDetection();
  const [searchParams] = useSearchParams();
  if (isMobile) {
    const view = searchParams.get('view');
    return <Navigate to={view === 'favoris' ? '/mobile/favorites' : '/mobile/catalog'} replace />;
  }
  return (
    <PublicLayout>
      <ServicesPage />
    </PublicLayout>
  );
};

const ProtectedRoute = ({ allowedRoles }: { allowedRoles: Role[] }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (!allowedRoles.includes(user.role)) return <Navigate to="/" replace />;
  return <Outlet />;
};

const PublicOnlyRoute = () => {
    const { user } = useAuth();
    if (user) {
        const dashboardPath = user.role === Role.ADMIN ? '/admin' : '/dashboard';
        return <Navigate to={dashboardPath} replace />;
    }
    return <Outlet />;
};

// Route publique accessible même si connecté (pour forgot-password, reset-password)
const PublicRoute = () => {
    return <Outlet />;
};

const AppRoutes = () => {
  const { user, loading } = useAuth();
  const { isMobile } = useDeviceDetection();
  const isNative = Capacitor.isNativePlatform();
  const [showSplash, setShowSplash] = useState(true);
  const [routeKey, setRouteKey] = useState(0);
  const location = ReactRouterDOM.useLocation();
  const navigate = ReactRouterDOM.useNavigate();

  // Gestion du bouton retour Android
  useAndroidBackButton();

  // Gestion du Splash Screen avec détection device
  useEffect(() => {
    // Sur plateforme native (Android/iOS via Capacitor), on ne rajoute PAS
    // de délai artificiel : le temps de chargement visuel est assuré par
    // le splash natif (SplashActivity). Sur desktop/web uniquement, on garde
    // une courte animation de splash React.
    const splashDelay = isNative ? 0 : 1500;

    const timer = setTimeout(() => {
      setShowSplash(false);

      // Redirection immédiate sur mobile si nécessaire
      if (isMobile) {
        // Routes publiques accessibles sur mobile (ne pas rediriger)
        // ⚠️ Inclure /verify-phone pour éviter une boucle
        //     /mobile/dashboard → RequirePhoneVerified → /verify-phone
        //     puis redirection automatique retour /mobile/dashboard
        const publicRoutes = [
          '/',            // Accueil publique
          '/login', 
          '/register', 
          '/forgot-password', 
          '/reset-password', 
          '/oauth/callback',
          '/verify-phone',
          '/verify-email',
          // Routes publiques accessibles sur mobile et desktop
          '/about',
          '/contact',
          // Routes légales accessibles sur mobile et desktop
          '/faq',
          '/terms',
          '/privacy',
          '/refund',
          '/delivery',
          '/legal',
          '/cookies',
          '/rate-limiting', // Limites d'utilisation et sécurité
        ];
        const isPublicRoute = publicRoutes.includes(location.pathname);
        
        // Si on est sur mobile et non authentifié, rediriger vers login (sauf routes publiques)
        if (!loading && !user && !location.pathname.startsWith('/mobile') && !isPublicRoute) {
          navigate('/login', { replace: true });
        }
        // Si on est sur mobile et authentifié mais pas sur une route mobile, rediriger (sauf routes publiques)
        else if (!loading && user && !location.pathname.startsWith('/mobile') && !isPublicRoute) {
          navigate('/mobile/dashboard', { replace: true });
        }
      }
    }, splashDelay);
    return () => clearTimeout(timer);
  }, [isMobile, loading, user, location.pathname, navigate]);

  // Afficher le Splash pendant le chargement ou l'animation
  // - Desktop : SplashScreen React complet (logo + version + statut OTA)
  // - Mobile (native) : simple fond (le vrai écran d'accueil est le splash natif Android)
  if (loading || showSplash) {
    if (isNative) {
      return (
        <div
          className="min-h-screen min-h-[100dvh] bg-gradient-to-b from-white to-[#f8fafc] dark:from-[#0f172a] dark:to-[#1e293b]"
          aria-busy="true"
        />
      );
    }
    return <SplashScreen />;
  }

  // Séparation complète des routes : Mobile vs Desktop
  // IMPORTANT: Les routes sont ordonnées par priorité
  // Les routes spécifiques (mobile) doivent être avant les routes générales
  const handleRetry = () => setRouteKey((k) => k + 1);

  return (
    <>
      <ScrollToTop />
      <React.Fragment key={routeKey}>
      <ErrorBoundary onRetry={handleRetry}>
      <Suspense
        fallback={
          isNative ? (
            <div className="min-h-screen min-h-[100dvh] bg-gradient-to-b from-white to-[#f8fafc] dark:from-[#0f172a] dark:to-[#1e293b]" aria-busy="true" />
          ) : (
            <SplashScreen />
          )
        }
      >
      <Routes>
        {/* ============================================
            ROUTES MOBILE (uniquement sur mobile)
            ============================================ */}
        <Route path="/mobile" element={<MobileOnlyRoute />}>
          <Route element={<MobileLayout />}>
            {/* Routes client mobile : email vérifié puis téléphone vérifié */}
            <Route element={<RequireEmailVerified />}>
              <Route element={<RequirePhoneVerified />}>
              <Route path="dashboard" element={<MobileDashboard />} />
              <Route path="catalog" element={<ProductCatalog />} />
              <Route path="wallet" element={<Wallet />} />
              <Route path="orders" element={<MobileOrders />} />
              <Route path="profile" element={<MobileProfile />} />
              <Route path="security" element={<MobileSecurity />} />
              <Route path="support" element={<MobileSupport />} />
              <Route path="product/:id" element={<MobileProductDetail />} />
              <Route path="favorites" element={<MobileFavorites />} />
              <Route path="cart" element={<MobileCart />} />
              <Route path="checkout" element={<MobileCheckout />} />
              <Route path="codes" element={<MobileMyCodes />} />
              <Route path="legal-documents" element={<MobileLegalDocuments />} />
              <Route path="contact" element={<MobileContactPage />} />
              <Route path="about" element={<MobileAboutPage />} />
            </Route>
            </Route>

            {/* Mobile Admin Routes : réservées aux utilisateurs avec rôle admin */}
            <Route element={<MobileAdminGuard />}>
              <Route path="admin/users" element={<MobileManageUsers />} />
              <Route path="admin/products" element={<MobileManageProducts />} />
              <Route path="admin/stock" element={<MobileManageStock />} />
              <Route path="admin/deposits" element={<MobileManageDeposits />} />
              <Route path="admin/orders" element={<MobileManageOrders />} />
              <Route path="admin/tickets" element={<MobileTicketClients />} />
              <Route path="admin/tickets/client/:clientId/:email" element={<MobileClientConversations />} />
              <Route path="admin/tickets/ticket/:ticketId" element={<MobileConversationMessages />} />
              <Route path="admin/categories" element={<MobileManageCategories />} />
              <Route path="admin/media" element={<MobileManageMedia />} />
            </Route>
          </Route>
        </Route>

        {/* ============================================
            ROUTES PUBLIQUES (accessibles sur mobile et desktop, même si connecté)
            ============================================ */}
        <Route element={<PublicRoute />}>
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
        </Route>

        {/* ============================================
            ROUTES DESKTOP (bloquées sur mobile)
            ============================================ */}
        
        {/* /services : sur mobile → catalogue/favoris, sur desktop → ServicesPage */}
        <Route path="/services" element={<ServicesPageOrRedirect />} />

        {/* Routes publiques desktop */}
        <Route element={<DeviceGuard redirectTo="/mobile/dashboard" />}>
          <Route element={<PublicLayout />}>
            <Route path="/" element={<PublicLandingRoute><LandingPage /></PublicLandingRoute>} />
            <Route path="/product/:id" element={<ProductDetailPage />} />
            <Route path="/cart" element={<CartPage />} />
          </Route>
        </Route>

        {/* Routes publiques - Accessibles sur mobile et desktop */}
        <Route element={<PublicLayout />}>
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
        </Route>

        {/* Routes légales - Accessibles sur mobile et desktop */}
        <Route path="/faq" element={<FaqPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/refund" element={<RefundPage />} />
        <Route path="/delivery" element={<DeliveryPage />} />
        <Route path="/legal" element={<LegalNoticePage />} />
        <Route path="/cookies" element={<CookiePolicyPage />} />
        <Route path="/rate-limiting" element={<RateLimitingPolicyPage />} />

        {/* Routes publiques desktop (Login/Register) - Accessibles aussi sur mobile */}
        <Route element={<PublicOnlyRoute />}>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/oauth/callback" element={<OAuthCallback />} />
        </Route>

        {/* Vérification téléphone : accessible sans auth (mode=new_classic/new_google + token) ou avec auth (client existant) */}
        <Route path="/verify-phone" element={<VerifyPhone />} />
        {/* Confirmation email : après inscription ou si connexion sans email vérifié */}
        <Route path="/verify-email" element={<VerifyEmail />} />

        {/* Dashboard Client Desktop */}
        <Route element={<DeviceGuard redirectTo="/mobile/dashboard" />}>
          <Route element={<ProtectedRoute allowedRoles={[Role.CLIENT]} />}>
            <Route element={<RequireEmailVerified />}>
              <Route element={<RequirePhoneVerified />}>
              <Route path="/dashboard" element={<ClientLayout />}>
                <Route index element={<Dashboard />} />
                <Route path="orders" element={<ClientOrders />} />
                <Route path="codes" element={<MyCodes />} />
                <Route path="deposits" element={<Deposits />} />
                <Route path="services" element={<ClientServices />} />
                <Route path="checkout" element={<ClientCheckout />} />
                <Route path="profile" element={<Profile />} />
                <Route path="settings" element={<ClientSettings />} />
                <Route path="support" element={<SupportTickets />} />
                <Route path="support/:ticketId" element={<TicketDetail />} />
              </Route>
            </Route>
            </Route>
          </Route>
        </Route>

        {/* Dashboard Admin Desktop */}
        <Route element={<DeviceGuard redirectTo="/mobile/dashboard" />}>
          <Route element={<ProtectedRoute allowedRoles={[Role.ADMIN]} />}>
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="deposits" element={<ManageDeposits />} />
              <Route path="orders" element={<ManageOrders />} />
              <Route path="products" element={<ManageProducts />} />
              <Route path="categories" element={<ManageCategories />} />
              <Route path="stock" element={<ManageStock />} />
              <Route path="media" element={<ManageMedia />} />
              <Route path="security" element={<SecurityDashboard />} />
              {/* Routes users : mettre la route paramétrée avant la route exacte */}
              <Route path="users/:userId" element={<UserDetail />} />
              <Route path="users" element={<ManageUsers />} />
              <Route path="settings" element={<Settings />} />
              <Route path="profile" element={<AdminProfile />} />
              <Route path="payment-methods" element={<ManagePaymentMethods />} />
              <Route path="delivery-settings" element={<DeliverySettings />} />
              <Route path="preferences" element={<Navigate to="/admin/payment-methods" replace />} />
              {/* Routes support : mettre la route paramétrée avant la route exacte */}
              <Route path="support/:ticketId" element={<AdminTicketDetail />} />
              <Route path="support" element={<ManageTickets />} />
            </Route>
          </Route>
        </Route>

        {/* 404 - Redirection selon device */}
        <Route
          path="*"
          element={isMobile ? <Navigate to="/mobile/dashboard" replace /> : <NotFound />}
        />
      </Routes>
      </Suspense>
      </ErrorBoundary>
      </React.Fragment>
    </>
  );
}

const App = () => {
  // Initialiser le service OTA (simple, non-bloquant)
  useEffect(() => {
    liveUpdateService.initialize();
  }, []);

  if (supabaseConfigurationError) return <div>Configuration Error: {supabaseConfigurationError.message}</div>;
  return (
    <ThemeProvider>
      <CartProvider>
        <AuthProvider>
          <NotificationProvider>
            <FavoritesProvider>
              <AppRoutes />
            </FavoritesProvider>
          </NotificationProvider>
        </AuthProvider>
      </CartProvider>
    </ThemeProvider>
  );
};

export default App;